import PaymentLayout from "../layouts/PaymentLinkLayouts";
import { useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { getVendorConfig } from "../config/ewalletVendors";
import useTranslation from "../hooks/useTranslation";

function normalizeStep(step) {
  const trimmed = step.trim();
  const isSubItem = trimmed.startsWith("-");
  const withoutNumber = trimmed.replace(/^\d+\.\s*/, "");
  const text = withoutNumber.replace(/^-\s*/, "");

  return { isSubItem, text };
}

function InstructionSteps({ content }) {
  let currentNumber = 0;

  return (
    <div className="space-y-1 text-sm font-normal leading-6 text-gray-600 md:text-base md:leading-7">
      {content.map((step) => {
        const { isSubItem, text } = normalizeStep(step);

        if (isSubItem) {
          return (
            <div key={step} className="ml-6 flex gap-2">
              <span aria-hidden="true">-</span>
              <span>{text}</span>
            </div>
          );
        }

        currentNumber += 1;

        return (
          <div key={step} className="flex gap-2">
            <span className="w-5 shrink-0">{currentNumber}.</span>
            <span>{text}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function PaymentInstructionEwallet({ onCheckStatus, ewalletName, ewalletCode, ewalletUrl, onBack }) {
  const { t } = useTranslation();
  const instructions = t("paymentInstructionEwallet.sections");
  const sections = instructions[ewalletCode] || [];
  const [activeSection, setActiveSection] = useState(sections[0]?.id || "");

  const ewalletConfig = getVendorConfig(ewalletCode) || getVendorConfig("EWALLET_SHOPEEPAY");
  const brandColor = ewalletConfig?.brandColor || "#008D40";
  const brandHoverColor = ewalletConfig?.brandHoverColor || brandColor;
  const displayName = ewalletConfig?.name || ewalletName || "E-Wallet";

  const handleEwalletPayment = () => {
    if (ewalletUrl) {
      window.open(ewalletUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <PaymentLayout hideNavbar unframedContent onBack={onBack}>
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <button
          type="button"
          onClick={handleEwalletPayment}
          style={{
            backgroundColor: brandColor,
            "--ewallet-button-hover": brandHoverColor,
          }}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold text-white transition active:scale-[0.99] hover:bg-[var(--ewallet-button-hover)]"
        >
          <ExternalLink size={18} strokeWidth={2.5} aria-hidden="true" />
          {t("paymentInstructionEwallet.payWith")} {displayName}
        </button>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {sections.map((section) => {
            const isActive = activeSection === section.id;

            return (
              <div key={section.id} className="border-b border-gray-200 last:border-b-0">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
                  onClick={() => setActiveSection(isActive ? "" : section.id)}
                >
                  <span className="text-sm font-semibold text-gray-900 md:text-base">{section.title}</span>
                  {isActive ? (
                    <ChevronUp size={18} className="shrink-0 text-gray-700" strokeWidth={2.5} />
                  ) : (
                    <ChevronDown size={18} className="shrink-0 text-gray-700" strokeWidth={2.5} />
                  )}
                </button>

                {isActive && (
                  <div className="px-4 pb-4">
                    <InstructionSteps content={section.content} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={onCheckStatus}
          className="mt-4 flex h-12 w-full items-center justify-center rounded-xl border border-singaHijauPrimary bg-white text-sm font-semibold text-singaHijauPrimary transition hover:bg-green-50"
        >
          {t("common.checkPaymentStatus")}
        </button>
      </div>
    </PaymentLayout>
  );
}
